import React from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Types
import { GoalItem } from 'Shared/constants/types';

// Actions
import { updateParentGoal } from 'Shared/actions/Goals.actions';

// Selectors
import { getGoalById, getGoalsByParentId } from 'Shared/selectors/Goals.selectors';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpCollapsible from 'Wpds/components/collapsible/WpCollapsible';
import GoalsTable, { GoalTableColumns } from 'Shared/components/Goals/GoalsTable/GoalsTable';
import { WpButton } from 'Wpds/components/button';
import AddSubGoal from '../AddSubGoal/AddSubGoal';

// Styles
import './GoalDetailsRelatedGoals.scss';

type Props = {
  goal: GoalItem;
  view?: 'parent-only' | 'sub-only' | 'all';
  collapsible?: boolean;
  showTitle?: boolean;
  showSubTitle?: boolean;
  slimRow?: boolean;
  onRowClick?: Function;
  hiddenColumns?: Array<GoalTableColumns>;
  goalTableComponents?: { [key in GoalTableColumns]?: Function };
};

function GoalDetailsRelatedGoals({
  goal,
  view = 'all',
  collapsible = true,
  slimRow = true,
  showTitle,
  showSubTitle = true,
  onRowClick,
  hiddenColumns = [GoalTableColumns.workspace, GoalTableColumns.progress, GoalTableColumns.department],
  goalTableComponents,
}: Props) {
  const { tr } = useTranslation();
  const dispatch = useDispatch();

  const [isOpen, toggleIsOpen] = useToggle(true);

  const { id, parentId } = goal;

  const parentGoal = useSelector(state => getGoalById(state, { id: parentId })?.toJS());
  const subGoals = useSelector(state => getGoalsByParentId(state, { parentId: id }).valueSeq().toJS());

  const handleUnlinkSubGoal = slug => {
    dispatch(updateParentGoal({ goal: { slug, parentId: null } }));
  };

  const handleUnlinkParentGoal = () => {
    dispatch(updateParentGoal({ goal: { slug: goal.slug, parentId: null } }));
  };

  const handleRowClick = (goal: GoalItem) => {
    onRowClick && onRowClick(goal);
  };

  const CustomSubLinkActions = ({ goal }) => {
    const [isRemoving, toggleIsRemoving] = useToggle(false);
    return (
      <WpButton
        onClick={() => {
          toggleIsRemoving();
          handleUnlinkSubGoal(goal.slug);
        }}
        size="sm"
        variant="tertiary"
        loading={isRemoving}
        qaTest={`unlink-children-goal-${goal.slug}`}
      >
        <WpIcon name="unlink" />
      </WpButton>
    );
  };

  const CustomParentLinkActions = () => {
    const [isRemoving, toggleIsRemoving] = useToggle(false);
    return (
      <WpButton
        onClick={() => {
          toggleIsRemoving();
          handleUnlinkParentGoal();
        }}
        size="sm"
        variant="tertiary"
        loading={isRemoving}
        qaTest={`unlink-parent-goal-${goal.slug}`}
      >
        <WpIcon name="unlink" />
      </WpButton>
    );
  };

  const baseClassName = 'GoalDetailsRelatedGoals';
  return (
    <div className={baseClassName}>
      {collapsible ? (
        <div className={`${baseClassName}__epxand-btn`}>
          <WpButton variant="tertiary" onClick={toggleIsOpen}>
            {tr('perform.goals.related_goals.linked_goals')}
            <WpIcon name={isOpen ? 'angle-down' : 'angle-right'} />
          </WpButton>
        </div>
      ) : (
        <>{showTitle ? <p>{tr('perform.goals.related_goals.linked_goals')}</p> : null}</>
      )}
      <WpCollapsible open={isOpen}>
        <>
          {parentGoal && (view === 'all' || view === 'parent-only') && (
            <div className={`${baseClassName}__section`}>
              {showSubTitle && (
                <span className={`${baseClassName}__section__label`}>
                  {tr('perform.goals.related_goals.parent_goal')}
                </span>
              )}
              <GoalsTable
                goals={[parentGoal]}
                hiddenColumns={hiddenColumns}
                showTableHeader={false}
                slimRow={slimRow}
                components={{ ...goalTableComponents, [GoalTableColumns.actions]: CustomParentLinkActions }}
                onRowClick={onRowClick && handleRowClick}
              />
            </div>
          )}
          {(view === 'all' || view === 'sub-only') && (
            <>
              <div className={`${baseClassName}__section ${showSubTitle ? 'mt-4' : ''}`}>
                {showSubTitle && (
                  <span className={`${baseClassName}__section__label`}>
                    {tr('perform.goals.related_goals.sub_goals')}
                  </span>
                )}
                {subGoals.length > 0 && (
                  <GoalsTable
                    goals={subGoals}
                    hiddenColumns={hiddenColumns}
                    showTableHeader={false}
                    components={{
                      ...goalTableComponents,
                      [GoalTableColumns.actions]: CustomSubLinkActions,
                    }}
                    slimRow={slimRow}
                    onRowClick={onRowClick && handleRowClick}
                  />
                )}
              </div>
              <div className="mt-2">
                <AddSubGoal goalId={id} />
              </div>
            </>
          )}
        </>
      </WpCollapsible>
    </div>
  );
}

export default GoalDetailsRelatedGoals;
